import moment from 'moment';
import { LevelRewardInput, LevelRewardInputType, RedeemCodeFragmentFragment } from '../../../../../__gqltypes__';
import { typeEmoji } from '../../components/formatReward';

export type RedeemCodeReward = RedeemCodeFragmentFragment['rewards'][number];

export const REDEEM_CODE_REGEX = /^[A-Z0-9]+$/;

export const isValidRedeemCode = (code: string): boolean => REDEEM_CODE_REGEX.test(code.trim());

export type EditableReward = LevelRewardInput & { itemName?: string };

const TYPENAME_ALIASES: Record<string, LevelRewardInputType> = {
  CurrencyUnlimitedBoost: LevelRewardInputType.CURRENCY_BOOST,
  CurrencyCategoryRandom: LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT,
};

const toLevelRewardInputType = (typename: string): LevelRewardInputType | null => {
  if (TYPENAME_ALIASES[typename]) {
    return TYPENAME_ALIASES[typename];
  }
  const normalized = typename
    .replace(/([A-Z])/g, '_$1')
    .replace(/^_/, '')
    .toUpperCase();
  const asEnum = normalized as LevelRewardInputType;
  return Object.values(LevelRewardInputType).includes(asEnum) ? asEnum : null;
};

// Maps a LevelReward union member (as queried) back to the LevelRewardInput shape used by EditRewardsModal
export const rewardUnionToInput = (reward: RedeemCodeReward): EditableReward | null => {
  const type = toLevelRewardInputType(reward.__typename);
  if (!type) {
    return null;
  }

  let itemId: string | null = null;
  if ('boostType' in reward && reward.boostType) {
    itemId = reward.boostType;
  } else if ('category' in reward && reward.category) {
    itemId = reward.category.id;
  } else if ('contentPack' in reward && reward.contentPack) {
    itemId = reward.contentPack.id;
  } else if ('id' in reward && reward.id) {
    itemId = reward.id;
  }

  return {
    type,
    amount: 'amount' in reward && typeof reward.amount === 'number' ? reward.amount : 1,
    itemId,
    ...('name' in reward && reward.name ? { itemName: reward.name } : {}),
  };
};

export const parseFilterUserIds = (raw: string): string[] | null => {
  const ids = raw
    .split(/[\n,;]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  return ids.length > 0 ? ids : null;
};

export const formatUtcDate = (date: number | Date | null | undefined): string =>
  date ? moment(date).utc().format('YYYY-MM-DD HH:mm') : '-';

export const formatLocalPreview = (date: Date | null | undefined): string =>
  date ? `${moment(date).local().format('YYYY-MM-DD HH:mm')} (${Intl.DateTimeFormat().resolvedOptions().timeZone})` : '';

// Short reward label for the list table, e.g. "💎 50" or "🎇 sticker_1"
export const compactRewardLabel = (reward: EditableReward): string => {
  const emoji = typeEmoji[reward.type as keyof typeof typeEmoji] ?? '';
  const detail = reward.type.startsWith('CURRENCY') ? `${reward.amount}` : reward.itemName ?? reward.itemId ?? reward.type;
  return `${emoji} ${detail}`.trim();
};

export const formatEligibilitySummary = (redeemCode: RedeemCodeFragmentFragment): string => {
  const parts: string[] = [];
  if (redeemCode.filterUserIds?.length) {
    parts.push(`${redeemCode.filterUserIds.length} user${redeemCode.filterUserIds.length > 1 ? 's' : ''}`);
  }
  if (redeemCode.filterPlatform) {
    parts.push(redeemCode.filterPlatform === 'IOS' ? 'iOS' : 'Android');
  }
  if (redeemCode.filterAppVersion) {
    parts.push(`v${redeemCode.filterAppVersion}+`);
  }
  if (redeemCode.filterSubscriptionTier) {
    parts.push(`${redeemCode.filterSubscriptionTier}+`);
  }
  return parts.length ? parts.join(' · ') : 'All players';
};

export type RedeemCodeStatusInfo = { label: string; variant: string };

export const getRedeemCodeStatus = (
  startDate: number | Date,
  endDate: number | Date | null | undefined
): RedeemCodeStatusInfo => {
  if (moment(startDate).isAfter(moment())) {
    return { label: 'Pending', variant: 'warning' };
  }
  // No end date = lifetime code, never expires
  if (endDate && moment(endDate).isBefore(moment())) {
    return { label: 'Expired', variant: 'secondary' };
  }
  return { label: 'Running', variant: 'success' };
};
